import { useEffect, useRef, useState } from "react";
import { Output } from "valibot";

import { BotInfoSchema } from "components/SonicBot/schemas";
import { useGlobalsContext } from "shared/hooks";
import classNames from "shared/utils/classNames";
import { ReactComponent as IconWidgetButtonDefault } from "assets/images/icons/widget-button-default.svg";

import "./index.css";

type Props = {
  botInfo: Output<typeof BotInfoSchema>;
  widgetPosition: string;
  useAsPreview: boolean;
  isLightAccent: boolean;
  chatHasMessages: boolean;
};

function WidgetButton(props: Props) {
  const {
    botInfo,
    widgetPosition,
    useAsPreview,
    isLightAccent,
    chatHasMessages,
  } = props;

  const { widgetExpanded, setWidgetExpanded, sendAnalyticsEvent } =
    useGlobalsContext();

  const [unreadCounter, setUnreadCounter] = useState(
    botInfo.bot_info_config.show_starter_widget ? 1 : 0,
  );

  const buttonRef = useRef<any>(null);

  useEffect(() => {
    if (!unreadCounter) {
      return;
    }

    const counterNeedsToBeReset =
      widgetExpanded ||
      !botInfo.bot_info_config.show_starter_widget ||
      chatHasMessages;

    if (counterNeedsToBeReset) {
      setUnreadCounter(0);
    }
  }, [
    unreadCounter,
    widgetExpanded,
    botInfo.bot_info_config.show_starter_widget,
    chatHasMessages,
  ]);

  const buttonIcon = botInfo.bot_info_config.button_icon;

  return (
    <div
      className={classNames(
        "widget-button-container",
        widgetExpanded && "hidden xs:block",
        widgetPosition !== "right" && !useAsPreview
          ? "justify-start"
          : "justify-end",
      )}
      onClick={() => {
        if (unreadCounter) {
          setUnreadCounter(0);
        }
        const newWidgetExpanded = !widgetExpanded;
        sendAnalyticsEvent(
          newWidgetExpanded ? "Messenger Opened" : "Messenger Closed",
        );
        setWidgetExpanded(newWidgetExpanded);
      }}
    >
      <div
        ref={buttonRef}
        className="widget-button bg-[var(--chatBotColor)] text-[var(--invertedColor)] active:scale-75"
      >
        <div className="relative">
          {widgetExpanded ? (
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                width={24}
                height={24}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </span>
          ) : (
            <div className="flex items-center">
              {buttonIcon ? (
                ["data:image", "http"].some(prefix =>
                  buttonIcon.startsWith(prefix),
                ) ? (
                  <img
                    src={buttonIcon}
                    className={classNames(
                      "h-9 w-9",
                      buttonIcon.endsWith(".svg") && !isLightAccent && "invert",
                    )}
                  />
                ) : (
                  <div
                    className={classNames(
                      "user-button-image",
                      buttonIcon.startsWith("<svg") && "text-black invert",
                    )}
                    dangerouslySetInnerHTML={{
                      __html: buttonIcon,
                    }}
                  />
                )
              ) : (
                <IconWidgetButtonDefault />
              )}
              {!!unreadCounter && (
                <div className="counter-badge">{unreadCounter}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WidgetButton;
